// Client i18n (PD-011, PD-012, PD-013). Locale comes from the signed-in user's preference, or —
// before sign-in — from the last choice on this device, then the browser language.
import { catalogues, dateTimeOptions, DEFAULT_DISPLAY_FORMAT, direction, intlLocale, isLocale, LOCALE_REVIEWED, negotiate, translate, type DisplayFormat, type Locale, type MessageKey, type MsgRef, type Params } from '@passage/i18n';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { setRequestLocale } from './api.ts';
import { demoCopy } from './demo-copy.ts'; // PD-819 truthful demo copy (demostaff stream)

const STORAGE_KEY = 'passage.locale';

// A shared kiosk must not remember one visitor's language for the next (PB-05-10).
let rememberOnDevice = true;
export function setRememberLocaleOnDevice(on: boolean): void {
  rememberOnDevice = on;
}

export interface I18n {
  readonly locale: Locale;
  readonly dir: 'ltr' | 'rtl';
  readonly reviewed: boolean;
  t(ref: MsgRef, params?: Params): string;
  has(key: string): key is MessageKey;
  /** Translate a computed key (e.g. `role.${id}.title`); falls back to `fallback` or the key. */
  tk(key: string, params?: Params, fallback?: string): string;
  date(value: string | Date, opts?: Intl.DateTimeFormatOptions): string;
  dateTime(value: string | Date, opts?: Intl.DateTimeFormatOptions): string;
  number(value: number): string;
  /** User choice: applied immediately and persisted through the registered account handler. */
  setLocale(locale: Locale): void;
  /** Adopt the signed-in account's stored language without persisting it again. */
  adopt(locale: Locale): void;
  /** Register (or clear) how a language change is saved for the signed-in account. */
  setPersist(fn: ((locale: Locale) => void) | null): void;
  /** OR-10 (PD-056): the organisation's date style and clock, from the signed-in session. */
  readonly display: DisplayFormat;
  setDisplay(f: DisplayFormat): void;
}

const Ctx = createContext<I18n | null>(null);

function isolate(params?: Params): Params | undefined {
  if (!params) return params;
  return Object.fromEntries(Object.entries(params).map(([k, v]) => [k, typeof v === 'string' && !v.startsWith('@') && !v.startsWith('\u2068') ? `\u2068${v}\u2069` : v]));
}

export function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  return negotiate(typeof navigator !== 'undefined' ? navigator.languages?.join(',') || navigator.language : undefined);
}

/** `demo`: PD-819 — in demo mode, success messages that claim something was saved or sent are swapped. */
export function I18nProvider({ children, demo = false }: { children: ReactNode; demo?: boolean }) {
  const [locale, setLocal] = useState<Locale>(initialLocale);
  const [display, setDisplayState] = useState<DisplayFormat>(DEFAULT_DISPLAY_FORMAT);
  const setDisplay = useCallback((f: DisplayFormat) => setDisplayState((cur) => (cur.dateStyle === f.dateStyle && cur.hourCycle === f.hourCycle ? cur : f)), []);
  const persist = useRef<((l: Locale) => void) | null>(null);

  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale === 'ar' ? 'ar' : 'en';
    root.dir = direction(locale);
    setRequestLocale(locale);
    if (!rememberOnDevice) return;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
  }, [locale]);

  // The request language changes before re-render, so queries keyed by locale fetch in the new one.
  const setLocale = useCallback((l: Locale) => {
    setRequestLocale(l);
    setLocal(l);
    persist.current?.(l);
  }, []);
  const adopt = useCallback((l: Locale) => {
    setRequestLocale(l);
    setLocal(l);
  }, []);
  const setPersist = useCallback((fn: ((l: Locale) => void) | null) => {
    persist.current = fn;
  }, []);

  const value = useMemo<I18n>(() => {
    const il = intlLocale(locale);
    const toDate = (v: string | Date) => (typeof v === 'string' ? new Date(v) : v);
    // Style defaults cannot be combined with explicit components (Intl throws), so explicit
    // component options replace the defaults; a bare timeZone keeps them.
    // OR-10: explicit components still follow the organisation's clock; the defaults follow its date style.
    const clock = (o: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions =>
      display.hourCycle !== 'locale' && (o.hour !== undefined || o.timeStyle !== undefined) && o.hourCycle === undefined && o.hour12 === undefined ? { ...o, hourCycle: display.hourCycle } : o;
    const fmt = (withTime: boolean, opts?: Intl.DateTimeFormatOptions) => {
      const explicit = opts && Object.keys(opts).some((k) => k !== 'timeZone');
      if (explicit) return (v: Date) => new Intl.DateTimeFormat(il, clock(opts)).format(v);
      const tz = opts?.timeZone ? { timeZone: opts.timeZone } : {};
      if (display.dateStyle === 'iso') {
        // Year-month-day in every language; the time keeps the language's digits and the organisation clock.
        const d = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...tz });
        const t = new Intl.DateTimeFormat(il, clock({ hour: '2-digit', minute: '2-digit', ...tz }));
        return (v: Date) => (withTime ? `${d.format(v)} ${t.format(v)}` : d.format(v));
      }
      const f = new Intl.DateTimeFormat(il, { ...dateTimeOptions(display, withTime), ...tz });
      return (v: Date) => f.format(v);
    };
    return {
      locale,
      dir: direction(locale),
      reviewed: LOCALE_REVIEWED[locale],
      // Interpolated strings (names, codes, emails) are wrapped in Unicode isolates so they never
      // reorder surrounding punctuation in mixed-direction text (PD-012).
      t: (ref, params) => translate(locale, demo ? demoCopy(ref) : ref, isolate(params)),
      has: (key): key is MessageKey => Object.prototype.hasOwnProperty.call(catalogues.en, key),
      tk: (key, params, fallback) => (Object.prototype.hasOwnProperty.call(catalogues.en, key) ? translate(locale, demo ? demoCopy(key as MessageKey) : (key as MessageKey), isolate(params)) : (fallback ?? key)),
      date: (v, opts) => fmt(false, opts)(toDate(v)),
      dateTime: (v, opts) => fmt(true, opts)(toDate(v)),
      number: (n) => new Intl.NumberFormat(il).format(n),
      setLocale,
      adopt,
      setPersist,
      display,
      setDisplay,
    };
  }, [locale, setLocale, adopt, setPersist, display, setDisplay, demo]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside I18nProvider');
  return v;
}

/** Language switch. Identifiers stay LTR; the Arabic draft is labelled unreviewed (H-10). */
export function LanguageSwitch({ compact }: { compact?: boolean }) {
  const { locale, setLocale, t } = useI18n();
  return (
    <div className="lang-switch" role="group" aria-label={t('lang.label')}>
      {(['en', 'ar'] as const).map((l) => (
        <button key={l} type="button" className={`button small${locale === l ? ' selected' : ' quiet'}`} aria-pressed={locale === l} lang={l} onClick={() => setLocale(l)}>
          {compact ? l.toUpperCase() : t(l === 'en' ? 'lang.en' : 'lang.ar')}
        </button>
      ))}
    </div>
  );
}

export function UnreviewedNotice() {
  const { reviewed, t } = useI18n();
  if (reviewed) return null;
  return (
    <span className="badge amber" role="note">
      {t('lang.unreviewed')}
    </span>
  );
}
